import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { colors, radii } from '../../theme/tokens';
import { Icon } from '../ui';
import { useToast } from '../Toast';
import { tasksApi } from '../../lib/tasksApi';
import { statusMeta, priorityMeta, daysLeft, checklistProgress, Avatar } from './taskHelpers';

// Per-user home for task tracking. Two tabs:
//  • New updates — tasks (grouped by department) with unseen changes made by
//    other users since you last opened them (new messages / status changes).
//  • My tasks — tasks you're assigned to, grouped by department.
// Clicking a task opens it in its department (onOpenTask).
const TaskDashboard = ({ onOpenTask }) => {
  const showToast = useToast();
  const [view, setView] = useState('updates');
  const [data, setData] = useState({ updates: [], mine: [] });
  const [loading, setLoading] = useState(true);
  const [hideDoneMine, setHideDoneMine] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tasksApi.dashboard();
      setData({ updates: res.updates || [], mine: res.mine || [] });
    } catch (_) {
      showToast('Failed to load dashboard', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const updateCount = useMemo(() => data.updates.reduce((n, g) => n + g.tasks.length, 0), [data.updates]);
  const mineCount = useMemo(() => data.mine.reduce((n, g) => n + g.tasks.length, 0), [data.mine]);
  const mineDoneCount = useMemo(
    () => data.mine.reduce((n, g) => n + g.tasks.filter((e) => e.task.status === 'done').length, 0),
    [data.mine]
  );

  // My tasks hides done by default; drop groups that empty out once filtered.
  const mineGroups = useMemo(() => {
    if (!hideDoneMine) return data.mine;
    return data.mine
      .map((g) => ({ ...g, tasks: g.tasks.filter((e) => e.task.status !== 'done') }))
      .filter((g) => g.tasks.length);
  }, [data.mine, hideDoneMine]);

  const groups = view === 'updates' ? data.updates : mineGroups;

  const pill = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px',
    borderRadius: radii.pill, border: `1px solid ${active ? 'rgba(47,182,92,0.55)' : colors.glassBorder}`,
    background: active ? 'rgba(47,182,92,0.16)' : colors.pill, color: colors.text,
    fontSize: 13.5, fontWeight: 700, cursor: 'pointer'
  });

  return (
    <div>
      <div style={styles.tabs}>
        <button style={pill(view === 'updates')} onClick={() => setView('updates')}>
          <Icon name="notifications" size={16} color={view === 'updates' ? colors.parGreenBright : colors.textMuted} />
          New updates
          {updateCount > 0 && <span style={styles.tabCount}>{updateCount}</span>}
        </button>
        <button style={pill(view === 'mine')} onClick={() => setView('mine')}>
          <Icon name="assignment_ind" size={16} color={view === 'mine' ? colors.parGreenBright : colors.textMuted} />
          My tasks
          {mineCount > 0 && <span style={styles.tabCountMuted}>{mineCount}</span>}
        </button>
        {view === 'mine' && mineDoneCount > 0 && (
          <button style={pill(!hideDoneMine)} onClick={() => setHideDoneMine((v) => !v)}>
            <Icon name={hideDoneMine ? 'visibility_off' : 'visibility'} size={16} color={colors.textMuted} />
            {hideDoneMine ? `Show done (${mineDoneCount})` : 'Hide done'}
          </button>
        )}
        <button style={styles.refresh} onClick={load} title="Refresh">
          <Icon name="refresh" size={18} color={colors.textMuted} />
        </button>
      </div>

      {loading ? (
        <div style={styles.state}>Loading…</div>
      ) : groups.length === 0 ? (
        <div style={styles.state}>
          <Icon name={view === 'updates' ? 'done_all' : 'inbox'} size={38} color={colors.textFaint} />
          <p style={{ marginTop: 10 }}>
            {view === 'updates' ? "You're all caught up — no unseen changes." : "You have no assigned tasks."}
          </p>
        </div>
      ) : (
        <div style={styles.groups}>
          {groups.map((g) => (
            <div key={g.department._id} style={styles.group}>
              <div style={styles.groupHead}>
                <Icon name={g.department.icon || 'folder'} size={18} color={colors.parGreenBright} />
                <span style={styles.groupName}>{g.department.name}</span>
                <span style={styles.groupCount}>{g.tasks.length}</span>
              </div>
              <div style={styles.rows}>
                {g.tasks.map((entry) => (
                  <DashboardRow
                    key={entry.task._id}
                    entry={entry}
                    isUpdate={view === 'updates'}
                    onClick={() => onOpenTask(g.department.slug, entry.task._id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DashboardRow = ({ entry, isUpdate, onClick }) => {
  const task = entry.task;
  const meta = statusMeta(task.status);
  const prio = priorityMeta(task.priority);
  const dl = daysLeft(task.deadline);
  const prog = checklistProgress(task);

  return (
    <button style={styles.row} onClick={onClick}>
      <span style={{ ...styles.prioDot, background: prio.color }} title={`Priority: ${prio.label}`} />
      <span style={styles.rowTitle}>{task.title}</span>

      <span style={styles.rowAvatars}>
        {(task.assignees || []).slice(0, 3).map((a) => <Avatar key={a._id} user={a} size={22} />)}
      </span>

      {isUpdate && entry.unread > 0 && (
        <span style={styles.unreadChip} title={`${entry.unread} new message${entry.unread === 1 ? '' : 's'}`}>
          <Icon name="chat" size={13} color="#fff" /> {entry.unread}
        </span>
      )}
      {isUpdate && (entry.kinds || []).includes('status') && (
        <span style={{ ...styles.kindChip, color: meta.color, borderColor: meta.color }} title="Status changed">
          <Icon name="sync" size={13} color={meta.color} /> {meta.label}
        </span>
      )}

      {prog.total > 0 && (
        <span style={styles.progPill} title={`${prog.done} of ${prog.total} checklist items done`}>
          <Icon name="checklist" size={13} color={prog.done === prog.total ? colors.parGreenBright : colors.textMuted} /> {prog.done}/{prog.total}
        </span>
      )}
      {dl && (
        <span style={{ ...styles.daysLeft, color: dl.color, borderColor: dl.color }}>
          <Icon name="event" size={13} color={dl.color} /> {dl.label}
        </span>
      )}
      {!isUpdate && (
        <span style={{ ...styles.statusBadge, color: meta.color, borderColor: meta.color }}>{meta.label}</span>
      )}
      <Icon name="chevron_right" size={20} color={colors.textMuted} />
    </button>
  );
};

const styles = {
  tabs: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  tabCount: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: '#f97316', color: '#fff', fontSize: 11, fontWeight: 800 },
  tabCountMuted: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: colors.pill, color: colors.textMuted, fontSize: 11, fontWeight: 800 },
  refresh: { marginLeft: 'auto', background: 'none', border: 'none', padding: 6, cursor: 'pointer', display: 'inline-flex', borderRadius: radii.sm },
  state: { textAlign: 'center', color: colors.textMuted, padding: '40px 16px' },
  groups: { display: 'flex', flexDirection: 'column', gap: 18 },
  group: {},
  groupHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  groupName: { fontSize: 14, fontWeight: 800, color: colors.text, textTransform: 'uppercase', letterSpacing: 0.4 },
  groupCount: { fontSize: 11.5, fontWeight: 800, color: colors.textMuted, background: colors.pill, borderRadius: 999, padding: '1px 8px' },
  rows: { display: 'flex', flexDirection: 'column', gap: 8 },
  row: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
    padding: '10px 12px', borderRadius: radii.md, border: `1px solid ${colors.glassBorder}`,
    background: colors.surfaceSoft, color: colors.text, cursor: 'pointer'
  },
  prioDot: { width: 12, height: 12, borderRadius: 999, flexShrink: 0, boxShadow: '0 0 0 2px rgba(255,255,255,0.12)' },
  rowTitle: { flex: 1, fontWeight: 700, fontSize: 14.5, color: colors.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowAvatars: { display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 },
  unreadChip: { display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0, padding: '2px 8px', borderRadius: 999, background: '#f97316', color: '#fff', fontSize: 11.5, fontWeight: 800 },
  kindChip: { display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0, padding: '2px 8px', borderRadius: 999, border: '1px solid', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' },
  progPill: { display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0, padding: '2px 8px', borderRadius: 999, background: colors.pill, color: colors.textMuted, fontSize: 11.5, fontWeight: 800 },
  daysLeft: { display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0, padding: '2px 8px', borderRadius: 999, border: '1px solid', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' },
  statusBadge: { fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999, border: '1px solid', whiteSpace: 'nowrap', flexShrink: 0 }
};

export default TaskDashboard;
